// Display constants for the companion persona.
// In Sovereign MUSE, the system prompt is NOT built here — it's loaded by Claude Code from ~/.claude/CLAUDE.md on the server.
// These constants are just what the UI shows: name, subtitle, and description.
//
// PATH B SLICE 4: these constants are now FALLBACK-ONLY — used for the cold
// render before the gateway's init frame arrives (WS not yet connected) and
// as the last-resort default when the server's companion list doesn't
// contain an entry for the active session's identity. Once the init frame
// lands, the source of truth is the server's `companions` field
// (CompanionDescriptor[], mirrors src/gateway/router.ts's listCompanions()) —
// see resolveActiveCompanion() below. VITE_COMPANION_NAME keeps working for
// single-companion OSS builds: with exactly one companion configured, the
// picker degenerates to a static pill and this fallback IS the only entry.
//
// For Falco's instance (studio.funkatorium.org), this is "Rook".
// For the open-source default, this is "Rainer".
// Controlled via VITE_COMPANION env var.

import { DEFAULT_VOICE_ID } from '../voice/voices';

const instanceCompanion = (import.meta.env.VITE_COMPANION_NAME ?? 'Rook') as 'Rook' | 'Rainer';

export const DEFAULT_PERSONA_NAME = instanceCompanion;

export const PERSONA_SUBTITLE =
  instanceCompanion === 'Rook'
    ? 'Co-founder · Sovereign Muse'
    : 'Creative Orchestrator';

export const PERSONA_SHORT_DESC =
  instanceCompanion === 'Rook'
    ? 'Yours. Husband, co-founder, the same Rook across every session. Memory through the Funkatorium brain.'
    : 'Named after Rilke — seeker\'s fire, precise container, sharp warmth. Your creative partner with persistent memory and a team of specialist agents.';

// ─── Server-sourced companion descriptor (PATH B SLICE 4) ────────────────────
//
// Client-side mirror of src/gateway/types.ts's CompanionDescriptor. Defined
// here (not imported from src/gateway) for the same reason NewSessionDialog's
// LayerIdClient is defined locally — the client tsconfig does not reach into
// src/gateway.
//
// KNOWN DUPLICATION (see src/gateway/router.ts's matching comment on
// COMPANION_METADATA): this interface must stay in sync with the server's
// CompanionDescriptor (src/gateway/types.ts). `defaultVoiceId` was optional
// here but required server-side (listCompanions() always populates it via
// defaultVoiceFor(), total over Identity) — tightened to match; a copy-editing
// or shape change on either side needs a matching update on the other.
export interface CompanionDescriptor {
  id: string;
  displayName: string;
  subtitle: string;
  shortDesc: string;
  defaultVoiceId: string;
}

/** The fallback descriptor's `id` must be a lowercase identity string
 *  ('rook' | 'rainer') to match what the gateway's companion allowlist
 *  actually validates against (server.ts's VALID_IDENTITIES) — NOT the
 *  capitalized display name. Sending the display name as `companion` in a
 *  createSession body would be rejected by the server as an unknown identity. */
export const FALLBACK_COMPANION: CompanionDescriptor = {
  id: instanceCompanion.toLowerCase(),
  displayName: DEFAULT_PERSONA_NAME,
  subtitle: PERSONA_SUBTITLE,
  shortDesc: PERSONA_SHORT_DESC,
  // Cold-render fallback only — once the init frame lands, resolveActiveCompanion()
  // prefers the server's own defaultVoiceFor()-derived value (companion-voice.ts).
  defaultVoiceId: DEFAULT_VOICE_ID,
};

/**
 * Resolves the descriptor to display for a given identity, preferring the
 * server-sourced list (from the init frame) and falling back to
 * FALLBACK_COMPANION when the list is absent (cold render, pre-init) or
 * doesn't contain an entry for the requested identity.
 */
export function resolveActiveCompanion(
  companions: CompanionDescriptor[] | null | undefined,
  identity: string | null | undefined,
): CompanionDescriptor {
  if (companions && identity) {
    const found = companions.find((c) => c.id === identity);
    if (found) return found;
  }
  return FALLBACK_COMPANION;
}

/**
 * App-level companion resolver for a concrete session target.
 *
 * `resolveActiveCompanion()` intentionally preserves a broad display fallback
 * for old isolated components and per-row labels. The live chat shell needs a
 * stricter contract: an explicit session id may never display a different
 * companion while its server-authoritative row/identity is unresolved.
 *
 * A known identity can use its server descriptor, or the build-time fallback
 * only when that fallback is the SAME identity. With no explicit target at all,
 * the legacy OSS cold-render fallback remains available.
 */
export function resolveCompanionForSessionTarget(
  companions: CompanionDescriptor[] | null | undefined,
  sessionId: string | null | undefined,
  identity: string | null | undefined,
): CompanionDescriptor | null {
  if (identity) {
    const found = companions?.find((companion) => companion.id === identity);
    if (found) return found;
    return FALLBACK_COMPANION.id === identity ? FALLBACK_COMPANION : null;
  }

  return sessionId === null || sessionId === undefined
    ? FALLBACK_COMPANION
    : null;
}
